import { useEffect, useRef } from 'react';

import { useAppSelector } from '../../hooks';
import { Record } from '../../modules/bin/utility';
import { hasTextEntryInResources } from '../template/templateHelper';

/**
 * Whether the API has demanded a password rotation this session has not satisfied.
 *
 * Read from the session rather than from state of its own: the flag is the server's answer and
 * arrives with every session payload, so there is nothing to keep in sync and no slice key a test
 * setup has to know to supply. Absent on an older API, where it reads as false and nothing happens,
 * which is what makes shipping this before core safe.
 */
export const useForcedPasswordReset = (): boolean =>
  useAppSelector((state) => state.session?.requiresPasswordReset) === true;

interface RecordOutcomeHandlers {
  onSuccess?: () => void;
  onFailure?: () => void;
}

/**
 * Fires once when a record crosses into success or failure, not on every render while it sits
 * there. Both ends need handling: on failure the verification modal has to come off the top of the
 * error, and on success the session has to be refetched or the wall stays up over a rotation that
 * has already been satisfied.
 */
export const useRecordOutcome = (
  record: any,
  { onSuccess, onFailure }: RecordOutcomeHandlers
) => {
  const wasSuccess = useRef(Record.isSuccess(record));
  const wasError = useRef(Record.isError(record));

  useEffect(() => {
    const isSuccess = Record.isSuccess(record);
    const isError = Record.isError(record);

    if (isSuccess && !wasSuccess.current) {
      onSuccess?.();
    }
    if (isError && !wasError.current) {
      onFailure?.();
    }

    wasSuccess.current = isSuccess;
    wasError.current = isError;
  }, [record, onSuccess, onFailure]);
};

/**
 * The reason a save was refused, from the codes the API returned. Falls back to a generic message,
 * which is what a crash or a dropped connection produces: there is no code to resolve, and saying
 * nothing leaves the user retrying the same password against the same failure.
 */
export const resolveSaveErrorKey = (
  errorKeys: string[] | null | undefined
): string => {
  const translated = (errorKeys ?? [])
    .map((key) => `error-messages.${key}`)
    .find(hasTextEntryInResources);

  return translated ?? 'general.general-error-message';
};
